/**
 * Tipos FHIR R4 (Fast Healthcare Interoperability Resources)
 * Subset simplificado para el sistema CGT
 * Basado en HL7 FHIR Release 4
 */

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR BASE TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRReference {
  reference?: string;
  type?: string;
  display?: string;
}

export interface FHIRCodeableConcept {
  coding?: FHIRCoding[];
  text?: string;
}

export interface FHIRCoding {
  system: string;
  code: string;
  display?: string;
  version?: string;
}

export interface FHIRQuantity {
  value?: number;
  comparator?: '<' | '>' | '<=' | '>=';
  unit?: string;
  system?: string;
  code?: string;
}

export interface FHIRRange {
  low?: FHIRQuantity;
  high?: FHIRQuantity;
}

export interface FHIRAnnotation {
  author?: FHIRReference | string;
  time?: string;
  text: string;
}

export interface FHIRIdentifier {
  use?: 'usual' | 'official' | 'temp' | 'secondary' | 'old';
  type?: FHIRCodeableConcept;
  system?: string;
  value?: string;
  period?: FHIRPeriod;
}

export interface FHIRPeriod {
  start?: string;
  end?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR PATIENT
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRPatient {
  resourceType: 'Patient';
  id?: string;
  identifier?: FHIRIdentifier[];
  active?: boolean;
  name?: FHIRHumanName[];
  telecom?: FHIRContactPoint[];
  gender?: 'male' | 'female' | 'other' | 'unknown';
  birthDate?: string;
  deceased?: boolean | string;
  address?: FHIRAddress[];
  maritalStatus?: FHIRCodeableConcept;
  contact?: FHIRPatientContact[];
  communication?: FHIRPatientCommunication[];
  generalPractitioner?: FHIRReference[];
}

export interface FHIRHumanName {
  use?: 'usual' | 'official' | 'temp' | 'nickname' | 'anonymous' | 'old' | 'maiden';
  text?: string;
  family?: string;
  given?: string[];
  prefix?: string[];
  suffix?: string[];
  period?: FHIRPeriod;
}

export interface FHIRContactPoint {
  system?: 'phone' | 'fax' | 'email' | 'pager' | 'url' | 'sms' | 'other';
  value?: string;
  use?: 'home' | 'work' | 'temp' | 'old' | 'mobile';
  rank?: number;
  period?: FHIRPeriod;
}

export interface FHIRAddress {
  use?: 'home' | 'work' | 'temp' | 'old' | 'billing';
  type?: 'postal' | 'physical' | 'both';
  text?: string;
  line?: string[];
  city?: string;
  district?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  period?: FHIRPeriod;
}

export interface FHIRPatientContact {
  relationship?: FHIRCodeableConcept[];
  name?: FHIRHumanName;
  telecom?: FHIRContactPoint[];
  address?: FHIRAddress;
  gender?: 'male' | 'female' | 'other' | 'unknown';
  organization?: FHIRReference;
  period?: FHIRPeriod;
}

export interface FHIRPatientCommunication {
  language: FHIRCodeableConcept;
  preferred?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR OBSERVATION (Signos vitales, Labs)
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRObservation {
  resourceType: 'Observation';
  id?: string;
  identifier?: FHIRIdentifier[];
  status: 'registered' | 'preliminary' | 'final' | 'amended';
  category?: FHIRCodeableConcept[];
  code: FHIRCodeableConcept;
  subject?: FHIRReference;
  encounter?: FHIRReference;
  effectiveDateTime?: string;
  effectivePeriod?: FHIRPeriod;
  issued?: string;
  performer?: FHIRReference[];
  valueQuantity?: FHIRQuantity;
  valueCodeableConcept?: FHIRCodeableConcept;
  valueString?: string;
  valueBoolean?: boolean;
  valueInteger?: number;
  valueRange?: FHIRRange;
  valueRatio?: FHIRRatio;
  valueTime?: string;
  valueDateTime?: string;
  valuePeriod?: FHIRPeriod;
  interpretation?: FHIRCodeableConcept[];
  note?: FHIRAnnotation[];
  bodySite?: FHIRCodeableConcept;
  method?: FHIRCodeableConcept;
  referenceRange?: FHIRObservationReferenceRange[];
  hasMember?: FHIRReference[];
  component?: FHIRObservationComponent[];
}

export interface FHIRRatio {
  numerator?: FHIRQuantity;
  denominator?: FHIRQuantity;
}

export interface FHIRObservationReferenceRange {
  low?: FHIRQuantity;
  high?: FHIRQuantity;
  type?: FHIRCodeableConcept;
  appliesTo?: FHIRCodeableConcept[];
  age?: FHIRRange;
  text?: string;
}

export interface FHIRObservationComponent {
  code: FHIRCodeableConcept;
  valueQuantity?: FHIRQuantity;
  valueCodeableConcept?: FHIRCodeableConcept;
  valueString?: string;
  valueBoolean?: boolean;
  valueRange?: FHIRRange;
  valueRatio?: FHIRRatio;
  interpretation?: FHIRCodeableConcept[];
  referenceRange?: FHIRObservationReferenceRange[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR CONDITION (Problemas/Diagnósticos)
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRCondition {
  resourceType: 'Condition';
  id?: string;
  identifier?: FHIRIdentifier[];
  clinicalStatus?: FHIRCodeableConcept;
  verificationStatus?: FHIRCodeableConcept;
  category?: FHIRCodeableConcept[];
  severity?: FHIRCodeableConcept;
  code?: FHIRCodeableConcept;
  bodySite?: FHIRCodeableConcept[];
  subject: FHIRReference;
  encounter?: FHIRReference;
  onsetDateTime?: string;
  onsetAge?: FHIRQuantity;
  onsetPeriod?: FHIRPeriod;
  onsetRange?: FHIRRange;
  onsetString?: string;
  abatementDateTime?: string;
  abatementAge?: FHIRQuantity;
  abatementPeriod?: FHIRPeriod;
  abatementRange?: FHIRRange;
  abatementString?: string;
  recordedDate?: string;
  recorder?: FHIRReference;
  asserter?: FHIRReference;
  stage?: FHIRConditionStage[];
  evidence?: FHIRConditionEvidence[];
  note?: FHIRAnnotation[];
}

export interface FHIRConditionStage {
  summary?: FHIRCodeableConcept;
  assessment?: FHIRReference[];
  type?: FHIRCodeableConcept;
}

export interface FHIRConditionEvidence {
  code?: FHIRCodeableConcept[];
  detail?: FHIRReference[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR ALLERGY INTOLERANCE
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRAllergyIntolerance {
  resourceType: 'AllergyIntolerance';
  id?: string;
  identifier?: FHIRIdentifier[];
  clinicalStatus?: FHIRCodeableConcept;
  verificationStatus?: FHIRCodeableConcept;
  type?: 'allergy' | 'intolerance';
  category?: ('food' | 'medication' | 'environment' | 'biologic')[];
  criticality?: 'low' | 'high' | 'unable-to-assess';
  code: FHIRCodeableConcept;
  patient: FHIRReference;
  onsetDateTime?: string;
  onsetAge?: FHIRQuantity;
  onsetPeriod?: FHIRPeriod;
  onsetRange?: FHIRRange;
  onsetString?: string;
  recordedDate?: string;
  recorder?: FHIRReference;
  asserter?: FHIRReference;
  lastOccurrence?: string;
  note?: FHIRAnnotation[];
  reaction?: FHIRAllergyReaction[];
}

export interface FHIRAllergyReaction {
  substance?: FHIRCodeableConcept;
  manifestation: FHIRCodeableConcept[];
  description?: string;
  onset?: string;
  severity?: 'mild' | 'moderate' | 'severe';
  exposureRoute?: FHIRCodeableConcept;
  note?: FHIRAnnotation[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR MEDICATION REQUEST
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRMedicationRequest {
  resourceType: 'MedicationRequest';
  id?: string;
  identifier?: FHIRIdentifier[];
  status: 'active' | 'on-hold' | 'cancelled' | 'completed' | 'entered-in-error' | 'stopped' | 'draft' | 'unknown';
  intent: 'proposal' | 'plan' | 'order' | 'original-order' | 'reflex-order' | 'filler-order' | 'instance-order' | 'option';
  category?: FHIRCodeableConcept[];
  priority?: 'routine' | 'urgent' | 'stat' | 'asap';
  medicationCodeableConcept?: FHIRCodeableConcept;
  medicationReference?: FHIRReference;
  subject: FHIRReference;
  encounter?: FHIRReference;
  authoredOn?: string;
  requester?: FHIRReference;
  performer?: FHIRReference;
  performerType?: FHIRCodeableConcept;
  recorder?: FHIRReference;
  reasonCode?: FHIRCodeableConcept[];
  reasonReference?: FHIRReference[];
  note?: FHIRAnnotation[];
  dosageInstruction?: FHIRDosage[];
}

export interface FHIRDosage {
  sequence?: number;
  text?: string;
  additionalInstruction?: FHIRCodeableConcept[];
  patientInstruction?: string;
  timing?: FHIRTiming;
  asNeededBoolean?: boolean;
  asNeededCodeableConcept?: FHIRCodeableConcept;
  site?: FHIRCodeableConcept;
  route?: FHIRCodeableConcept;
  method?: FHIRCodeableConcept;
  doseAndRate?: FHIRDoseAndRate[];
  maxDosePerPeriod?: FHIRRatio;
  maxDosePerAdministration?: FHIRQuantity;
  maxDosePerLifetime?: FHIRQuantity;
}

export interface FHIRTiming {
  event?: string[];
  repeat?: FHIRTimingRepeat;
  code?: FHIRCodeableConcept;
}

export interface FHIRTimingRepeat {
  boundsDuration?: FHIRQuantity;
  boundsRange?: FHIRRange;
  boundsPeriod?: FHIRPeriod;
  count?: number;
  countMax?: number;
  duration?: number;
  durationMax?: number;
  durationUnit?: 's' | 'min' | 'h' | 'd' | 'wk' | 'mo' | 'a';
  frequency?: number;
  frequencyMax?: number;
  period?: number;
  periodMax?: number;
  periodUnit?: 's' | 'min' | 'h' | 'd' | 'wk' | 'mo' | 'a';
  dayOfWeek?: string[];
  timeOfDay?: string[];
  when?: string[];
  offset?: number;
}

export interface FHIRDoseAndRate {
  type?: FHIRCodeableConcept;
  doseRange?: FHIRRange;
  doseQuantity?: FHIRQuantity;
  rateRatio?: FHIRRatio;
  rateRange?: FHIRRange;
  rateQuantity?: FHIRQuantity;
}

// ═══════════════════════════════════════════════════════════════════════════════
// FHIR IMAGING STUDY
// ═══════════════════════════════════════════════════════════════════════════════

export interface FHIRImagingStudy {
  resourceType: 'ImagingStudy';
  id?: string;
  identifier?: FHIRIdentifier[];
  status: 'registered' | 'available' | 'cancelled' | 'entered-in-error' | 'unknown';
  modality?: FHIRCoding[];
  subject: FHIRReference;
  encounter?: FHIRReference;
  started?: string;
  basedOn?: FHIRReference[];
  referrer?: FHIRReference;
  interpreter?: FHIRReference[];
  endpoint?: FHIRReference[];
  numberOfSeries?: number;
  numberOfInstances?: number;
  procedureReference?: FHIRReference;
  procedureCode?: FHIRCodeableConcept[];
  location?: FHIRReference;
  reasonCode?: FHIRCodeableConcept[];
  reasonReference?: FHIRReference[];
  note?: FHIRAnnotation[];
  description?: string;
  series?: FHIRImagingSeries[];
}

export interface FHIRImagingSeries {
  uid: string;
  identifier?: FHIRIdentifier[];
  number?: number;
  modality: FHIRCoding;
  description?: string;
  numberOfInstances?: number;
  endpoint?: FHIRReference[];
  bodySite?: FHIRCoding;
  specimen?: FHIRReference[];
  started?: string;
  performer?: FHIRImagingPerformer[];
  instance?: FHIRImagingInstance[];
}

export interface FHIRImagingPerformer {
  function?: FHIRCodeableConcept;
  actor: FHIRReference;
}

export interface FHIRImagingInstance {
  uid: string;
  sopClass: FHIRCoding;
  number?: number;
  title?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SISTEMAS DE CÓDIGOS
// ═══════════════════════════════════════════════════════════════════════════════

export const SNOMED_CT_SYSTEM = 'http://snomed.info/sct';
export const LOINC_SYSTEM = 'http://loinc.org';
export const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10';
export const RXNORM_SYSTEM = 'http://www.nlm.nih.gov/research/umls/rxnorm';

// Conceptos SNOMED CT comunes
export const SNOMED_CONCEPTS = {
  // Signos vitales
  HEART_RATE: '8867-4',
  BLOOD_PRESSURE: '85354-9',
  RESPIRATORY_RATE: '9279-1',
  BODY_TEMPERATURE: '8310-5',
  OXYGEN_SATURATION: '2708-6',
  
  // Síntomas cardiovasculares
  CHEST_PAIN: '279035001',
  CHEST_PAIN_PRESSING: '274668005',
  DYSPNEA: '267036007',
  ORTHOPNEA: '62744007',
  
  // Síntomas neurológicos
  CONFUSION: '40917007',
  ALTERED_MENTAL_STATUS: '4064161',
  HEADACHE: '25064002',
  SEIZURE: '91175000',
  
  // Síntomas generales
  FEVER: '386661006',
  FATIGUE: '84229001',
  NAUSEA: '422587007',
  
  // Condiciones
  MYOCARDIAL_INFARCTION: '22298006',
  SEPSIS: '91302008',
  PNEUMONIA: '233604007',
  STROKE: '230690007',
} as const;

// Códigos LOINC comunes
export const LOINC_CODES = {
  // Signos vitales
  HEART_RATE: '8867-4',
  SYSTOLIC_BP: '8480-6',
  DIASTOLIC_BP: '8462-4',
  RESPIRATORY_RATE: '9279-1',
  TEMPERATURE: '8310-5',
  SPO2: '2708-6',
  
  // Laboratorio
  TROPONIN_T: '6598-7',
  TROPONIN_I: '10839-9',
  CREATININE: '2160-0',
  BUN: '3094-0',
  GLUCOSE: '2345-7',
  LACTATE: '1968-7',
  WBC: '6690-2',
  HEMOGLOBIN: '718-7',
  PLATELETS: '777-3',
  CRP: '1988-5',
  PROCALCITONIN: '33959-8',
  
  // Gases arteriales
  PH_ARTERIAL: '2744-1',
  PCO2_ARTERIAL: '2019-8',
  PO2_ARTERIAL: '2703-7',
  HCO3_ARTERIAL: '1960-6',
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// UTILIDADES DE CONVERSIÓN
// ═══════════════════════════════════════════════════════════════════════════════

export function createCoding(
  system: string,
  code: string,
  display?: string,
  version?: string
): FHIRCoding {
  return { system, code, display, version };
}

export function createCodeableConcept(
  coding: FHIRCoding | FHIRCoding[],
  text?: string
): FHIRCodeableConcept {
  return {
    coding: Array.isArray(coding) ? coding : [coding],
    text
  };
}

export function createQuantity(
  value: number,
  unit: string,
  system?: string,
  code?: string,
  comparator?: '<' | '>' | '<=' | '>='
): FHIRQuantity {
  return { value, unit, system, code, comparator };
}

export function getDisplay(coding: FHIRCodeableConcept | undefined): string {
  if (!coding) return '';
  if (coding.text) return coding.text;
  if (coding.coding && coding.coding.length > 0) {
    return coding.coding[0].display || coding.coding[0].code;
  }
  return '';
}
